import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import FeaturedIn from "./FeaturedIn";
import { RESOURCES } from "./resourcesData";
import { SOCIAL, TAGLINES, HERO_LOGO_URL } from "./constants";
import {
  Sparkles, BookOpen, Shield, Users, Clock, Wind,
  ArrowRight, Apple, Lock, EyeOff, MapPin, Hand,
} from "lucide-react";

const FEATURES = [
  { icon: Sparkles, title: "Decode confusing messages",
    body: "AI-assisted reflection tools for texts, voicemails, and conversations that left you spinning.",
    to: "/resources" },
  { icon: BookOpen, title: "Understand relationship patterns",
    body: "Learn about gaslighting, DARVO, coercive control, trauma bonds, love bombing, and the Cycle of Abuse.",
    to: "/resources" },
  { icon: Shield, title: "Create a safety plan",
    body: "Build practical safety plans and document important information privately, on your own terms.",
    to: "/safety-privacy" },
  { icon: Users, title: "Trusted Circle",
    body: "Code words, emergency phrases, and trusted contacts who can be reached with one tap.",
    to: "/app" },
  { icon: Clock, title: "Date & outing check-ins",
    body: "Schedule a quiet check-in when meeting someone. If you don't respond, your circle is alerted.",
    to: "/app" },
  { icon: Wind, title: "Sanctuary",
    body: "Grounding exercises, breath work, and reflection tools for nervous-system recovery.",
    to: "/app" },
];

const SAFETY_BULLETS = [
  { icon: Lock,   h: "Privacy-first design",      body: "What you write stays in your account. We collect the minimum needed to serve you." },
  { icon: EyeOff, h: "Optional anonymity",        body: "Use Sister Signal without an email or name. A device identifier is enough." },
  { icon: Hand,   h: "Quick Exit",                body: "One tap disguises the app as a calculator. Useful when someone walks into the room." },
  { icon: MapPin, h: "You control your information", body: "Export, delete, or pause your data at any time. No dark patterns." },
];

export default function MarketingHome() {
  return (
    <MarketingShell>
      <Seo path="/" />
      {/* HERO ---------------------------------------------------------------- */}
      <section className="relative overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-brand-soft/60 via-background to-background" />
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10 pb-14 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <p className="font-script text-2xl text-brand">a private companion</p>
            <h1 className="mt-2 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
              <span className="text-brand">Clarity</span> is protection.
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-foreground/75 leading-relaxed max-w-xl">
              A private emotional safety companion designed to help people recognise unhealthy relationship patterns,
              strengthen personal safety, and make informed decisions with confidence.
            </p>
            <p className="mt-3 text-sm uppercase tracking-widest text-foreground/55">{TAGLINES.secondary}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href={SOCIAL.appStore} target="_blank" rel="noopener noreferrer"
                 data-testid="hero-app-store"
                 className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground text-background hover:bg-foreground/90 transition-all">
                <Apple className="w-5 h-5" />
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-widest opacity-70">Download on the</span>
                  <span className="block font-medium text-sm">App Store</span>
                </span>
              </a>
              <button
                disabled
                data-testid="hero-android"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-card border border-border text-foreground/55 cursor-not-allowed"
              >
                <span className="text-left leading-tight">
                  <span className="block text-[10px] uppercase tracking-widest">Android</span>
                  <span className="block font-medium text-sm">Coming soon</span>
                </span>
              </button>
              <Link to="/app" data-testid="hero-open-app"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all">
                Open Web App <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Brand illustration — aspect tuned per breakpoint so the pink
              container doesn't dominate the mobile viewport. */}
          <div className="relative order-first lg:order-last">
            <div className="aspect-[16/10] sm:aspect-[4/3] lg:aspect-square rounded-[2.5rem] bg-brand-soft/60 grid place-items-center overflow-hidden shadow-[0_30px_60px_-20px_rgba(214,90,140,0.25)] p-4 sm:p-6">
              <img
                src={HERO_LOGO_URL}
                alt="Sister Signal — Clarity is protection"
                className="w-full h-full object-contain"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Credibility — "Featured In" badge row */}
      <FeaturedIn />

      {/* WHAT SISTER SIGNAL DOES --------------------------------------------- */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20" data-testid="section-features">
        <div className="max-w-2xl">
          <p className="font-script text-2xl text-brand">what it does</p>
          <h2 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">A quiet toolkit for clarity, safety, and emotional steadiness.</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <Link
              key={f.title}
              to={f.to}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group rounded-3xl bg-card border border-border p-7 hover:border-brand/50 hover:shadow-md transition-all"
            >
              <div className="w-11 h-11 grid place-items-center rounded-full bg-brand/10 text-brand">
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-serif text-2xl leading-snug">{f.title}</h3>
              <p className="mt-2 text-foreground/70 leading-relaxed">{f.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* EDUCATIONAL RESOURCES ----------------------------------------------- */}
      <section className="bg-brand-soft/25 border-y border-brand/10" data-testid="section-resources">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="font-script text-2xl text-brand">learn the language</p>
              <h2 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">Words for what may have felt nameless.</h2>
              <p className="mt-4 text-foreground/75 leading-relaxed">
                Many survivors say recognition came when they finally had language for what they were experiencing.
                Each topic below opens to a short, trauma-informed primer — written for people, not lawyers.
              </p>
            </div>
            <Link to="/resources" data-testid="resources-all"
              className="inline-flex items-center gap-1 text-brand hover:underline underline-offset-4">
              Browse all resources <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RESOURCES.map((r) => (
              <Link
                key={r.slug}
                to={`/resources/${r.slug}`}
                data-testid={`resource-card-${r.slug}`}
                className="rounded-2xl bg-card border border-border p-6 hover:border-brand/50 transition-all"
              >
                <p className="text-xs uppercase tracking-widest text-foreground/55">{r.kicker}</p>
                <h3 className="mt-1 font-serif text-2xl leading-snug">{r.title}</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{r.lede}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION ------------------------------------------------------------- */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-24 text-center" data-testid="section-mission">
        <p className="font-script text-2xl text-brand">our mission</p>
        <h2 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">
          Sister Signal was created from lived experience.
        </h2>
        <p className="mt-6 text-lg text-foreground/75 leading-relaxed">
          We provide accessible tools, education, and emotional clarity for individuals navigating confusing,
          unhealthy, or emotionally unsafe relationships. We believe information, self-trust, and awareness can
          help people make empowered decisions about their future.
        </p>
        <Link to="/about" data-testid="mission-read-more"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all">
          Read the full story <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* SAFETY & PRIVACY ---------------------------------------------------- */}
      <section className="bg-foreground text-background" data-testid="section-safety">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-script text-2xl text-brand-soft">designed quietly</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-2 leading-tight">Built for privacy. By design, not by accident.</h2>
            <p className="mt-5 text-background/80 leading-relaxed">
              Sister Signal is a safety tool first. Every design decision is filtered through one question:
              <em> would this be safe if someone took the phone right now?</em>
            </p>
            <Link to="/safety-privacy" data-testid="safety-read-more"
              className="mt-8 inline-flex items-center gap-1 text-brand-soft hover:text-white underline-offset-4 hover:underline">
              Full safety & privacy commitments <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {SAFETY_BULLETS.map((s) => (
              <div key={s.h} className="rounded-2xl bg-background/5 border border-background/10 p-5">
                <div className="w-9 h-9 grid place-items-center rounded-full bg-brand/20 text-brand-soft">
                  <s.icon className="w-4 h-4" />
                </div>
                <h3 className="mt-3 font-serif text-xl">{s.h}</h3>
                <p className="mt-1 text-sm text-background/70 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS + DONATE DUAL CTA ------------------------------------------ */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20 grid md:grid-cols-2 gap-6" data-testid="section-cta">
        <Link to="/partners" data-testid="cta-partners"
          className="rounded-3xl bg-brand-soft/40 border border-brand/15 p-8 hover:border-brand/50 transition-all">
          <p className="font-script text-xl text-brand">collaborate</p>
          <h3 className="mt-1 font-serif text-3xl leading-tight">For DV organizations, shelters, and advocates.</h3>
          <p className="mt-3 text-foreground/75">If your work helps survivors, we'd love to find ways to support each other.</p>
          <span className="mt-5 inline-flex items-center gap-1 text-brand">Get in touch <ArrowRight className="w-4 h-4" /></span>
        </Link>
        <Link to="/donate" data-testid="cta-donate"
          className="rounded-3xl bg-card border border-border p-8 hover:border-brand/50 transition-all">
          <p className="font-script text-xl text-brand">support the mission</p>
          <h3 className="mt-1 font-serif text-3xl leading-tight">Donate to keep Sister Signal free.</h3>
          <p className="mt-3 text-foreground/75">Every gift helps a survivor access tools, education, and clarity without paying a cent.</p>
          <span className="mt-5 inline-flex items-center gap-1 text-brand">Give a gift <ArrowRight className="w-4 h-4" /></span>
        </Link>
      </section>
    </MarketingShell>
  );
}
